import {
  Component,
  ElementRef,
  Input,
  OnChanges,
  SimpleChanges,
  ViewChild,
  AfterViewInit,
  HostListener,
  ViewChildren,
  QueryList,
  signal,
  ChangeDetectionStrategy,
  ViewEncapsulation,
  Output,
  EventEmitter
} from '@angular/core';
import { TableColumn, TableConfig } from './phoenix-table.interface';
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import * as moment from 'moment';

@Component({
  selector: 'app-phoenix-table',
  templateUrl: './phoenix-table.component.html',
  styleUrls: ['./phoenix-table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.ShadowDom
})

export class PhoenixTableComponent<T> implements AfterViewInit, OnChanges {
  @Input() config?: TableConfig<T>;
  @Input() data: T[] = [];
  @Input() header: string = 'Add Table header';
  @Input() isLoading: boolean;
  @Input() showResults: boolean = true;

  @Output() dateChanged = new EventEmitter<{date: string, row: any}>();
  @Output() dateError = new EventEmitter<string>();
  @Output() dollarAmountChanged = new EventEmitter<{amount: string, row: any}>();

  @ViewChild('table') table: ElementRef<HTMLTableElement>;
  @ViewChild('container') container: ElementRef<HTMLDivElement>; // used for overflow detection
  @ViewChildren('dataRow') dataRows: QueryList<ElementRef<HTMLTableRowElement>>;
  @ViewChild(CdkVirtualScrollViewport, { static: false }) viewPort: CdkVirtualScrollViewport;

  isOverflowing: boolean = false;
  showInitial: boolean = true; // Display message of inital state of the table.
  sortColumn: string = '';
  sortDirection: 'asc' | 'desc' = 'asc';
  selectedRow: T; // For 'radio' selection mode
  selectedRows: Set<T> = new Set(); // For 'checkbox' selection mode
  isAllSelected: boolean = false; // this is for checkbox allselection
  dataResultsCount: number;

  protected _loading: boolean;
  protected showEmptyMessage = false;
  protected isReset = true;

  get cdkTranslation(): string {
    if (!this.viewPort) {
      return '0px';
    }
    const offset = this.viewPort.getOffsetToRenderedContentStart();

    return `${offset}px`;
  }

  averageRowHeight = signal(50);

  ngAfterViewInit(): void {
    setTimeout(() => this.checkTableOverflow(), 0);

    // value emitted when rendered rows are changed
    this.viewPort.renderedRangeStream.subscribe(() => {
      this.averageRowHeight.update(() => {
        if (!this.dataRows) {
          return 50;
        } else {
          let combinedRowHeight = 0;

          for (const row of this.dataRows) {
            combinedRowHeight += row.nativeElement.clientHeight;
          }

          return (combinedRowHeight / this.dataRows.length) || 50;
        }
      });

      setTimeout(() => {
        this.dataRows?.forEach(rowElement => {
          const row = rowElement.nativeElement;
          const infoMessage = row.querySelector('div.info-message');
          let messageHeight = (infoMessage?.getBoundingClientRect().height || 0);
          messageHeight = messageHeight > 0 ? messageHeight * 0.75 : 0; // Reduce height by 25%
          const heightPx = `${messageHeight}px`;
          row.querySelectorAll('td').forEach(td => {
            td.style.paddingBottom = `calc(0.2rem + ${heightPx})`;
          });
        });
      }, 50);
    });
  }

  @HostListener('window:load')
  onWindowLoad() {
    this.checkTableOverflow();
  }

  @HostListener('window:resize')
  onResize() {
    this.checkTableOverflow();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['config'] && this.config && this.config.columns.length > 0) {
      // Set the first sortable column as the default sort column
      this.setDefaultSort();
    }

    this._loading = changes.isLoading?.currentValue ?? false;

    if (changes['data']) {
      if (!changes.data.isFirstChange()) {
        this.showInitial = false;
      }

      this.dataResultsCount = this.data?.length || 0;

      if (changes.data.previousValue === undefined && this.isReset) {
        this.isReset = false;

        if (this.data !== undefined) {
          this.showInitial = false;
        }
      }

      this.selectedRows.clear();
      this.selectedRow = null;
      this.showEmptyMessage = !this.dataResultsCount; // Show empty message only when there's no data
      this.isLoading = false;
      this.refreshViewport();

      if (this.dataResultsCount > 0) {
        this.setDefaultSort();
        this.sortData();
        setTimeout(() => {
          this.refreshViewport();
        }, 100);
      } else {
        this.sortColumn = '';
        this.sortDirection = 'asc';
        this.refreshViewport(); // this ensures viewport reacts to empty data
      }
    }
  }

  private refreshViewport(): void {
    if (this.viewPort) {
      if (this.data && this.data.length > 0) {
        // Enable scrolling if there is data
        this.viewPort.checkViewportSize();
        this.viewPort.scrollToIndex(0);
        const range = this.viewPort.getRenderedRange();
        this.viewPort.setRenderedRange({ start: 1, end: 2 });
        this.viewPort.setRenderedRange(range);
      } else {
        // Disable scrolling if there is no data
        this.viewPort.setTotalContentSize(0);
        this.viewPort.checkViewportSize();
        this.viewPort.scrollToIndex(0);
        this.viewPort.setTotalContentSize(0);
      }
    }
  }

  public fixRendering(): void {
    // TODO: this is very hacky and should be fixed in the future.
    // this is a fix for when the table doesn't render all items in
    // the dataset, leaving empty space at the bottom on the table
    setTimeout(() => {
      this.refreshViewport();
    }, 0);
  }

  protected onSelectAllToggle(event: Event): void {
    const isChecked = (event.target as HTMLInputElement).checked;
    this.isAllSelected = isChecked; // Track "Select All" state, if needed

    if (isChecked) {
      // Select all rows
      this.data.forEach(row => this.selectedRows.add(row));
    } else {
      // Deselect all rows
      this.selectedRows.clear();
    }
  }

  protected toggleSort(columnKey: string): void {
    if (this.sortColumn === columnKey) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = columnKey;
      this.sortDirection = 'asc';
    }
    this.sortData();
  }

  private sortData(): void {
    if (!this.data || !this.sortColumn || !this.config) return;

    // Error handling if column does not exist.
    const columnExists = this.config.columns.some(col => col.key === this.sortColumn);

    if (!columnExists) {
      console.error(`Sort Error:  On column "${this.sortColumn}" does not exist in the table configuration.`);
      return;
    }

    // Recommended error check for all scenarios based on columns configuration
    try {
      const sortFunction = (a: T, b: T, col: string) => {
        let aa = a[col];
        let bb = b[col];

        // handle undefined or null values
        if (aa === undefined || aa === null) aa = '';
        if (bb === undefined || bb === null) bb = '';

        // for numeric values
        if (!isNaN(Number(aa)) && !isNaN(Number(bb))) {
          aa = Number(aa);
          bb = Number(bb);
        }

        if (aa < bb) return this.sortDirection === 'asc' ? -1 : 1;
        if (aa > bb) return this.sortDirection === 'asc' ? 1 : -1;
        return 0;
      };

      const sortedColumn = this.config.columns.find(i => i.key === this.sortColumn);

      // sort date columns
      if (sortedColumn.sortAsDate) {
        this.data.sort((a, b) => {
          const aa = moment(this.convertToIso8601(a[this.sortColumn]));
          const bb = moment(this.convertToIso8601(b[this.sortColumn]));

          if (!aa.isValid()) return this.sortDirection === 'asc' ? -1 : 1;
          if (!bb.isValid()) return this.sortDirection === 'asc' ? 1 : -1;

          if (aa.isBefore(bb)) {
            return this.sortDirection === 'asc' ? -1 : 1;
          } else if (aa.isAfter(bb)) {
            return this.sortDirection === 'asc' ? 1 : -1;
          } else {
            return 0;
          }
        });
      } else { // all other columns
        // secondary sorts
        if (sortedColumn.secondarySort) {
          for (const sort of sortedColumn.secondarySort.reverse()) {
            this.data.sort((a, b) => sortFunction(a, b, sort.toString()));
          }
        }

        // primary sort
        this.data.sort((a, b) => sortFunction(a, b, this.sortColumn));

        if (this.viewPort) {
          this.viewPort.scrollToIndex(0);
        }
      }

      // trigger change detection
      this.data = [...this.data];
    } catch (error) {
      console.error(`Sort Error: ${error}`);
      // We can add toast message here in the future .. Cory?
    }
  }

  private setDefaultSort(): void {
    this.sortColumn = this.config.defaultColumnSort;
    this.sortDirection = this.config.defaultSortOrder; // Set to ascending order by default
  }

  protected checkArrayType(data: any) {
    return Array.isArray(data);
  }

  protected checkTableOverflow() {
    const container = this.container.nativeElement;
    this.isOverflowing = container.scrollWidth > container.offsetWidth;
  }

  protected onCheckboxChange(event: Event, row: T): void {
    const element = event.target as HTMLInputElement;
    const isChecked = element.checked;

    if (isChecked) {
      this.selectedRows.add(row);
    } else {
      this.selectedRows.delete(row);
    }
  }

  protected getColumnSpan() {
    return this.config?.columns.length + (this.config?.hasActions ? 1 : 0) + (this.config?.selectionMode ? 1 : 0);
  }

  private convertToIso8601(date: string): string {
    const year = date.substring(6);
    const month = date.substring(0, 2);
    const day = date.substring(3, 5);
    return `${year}-${month}-${day}`;
  }

  public reset(): void {
    this.data = undefined;
    this.refreshViewport();
    this.isReset = true;
  }

  public selectRow(index: number): void {
    this.selectedRow = this.data[index];
  }

  protected makeIconStyle(col: TableColumn<T>, row: T): string {
    const icon = col.icon(row);
    let style = `mask: url('${icon.icon}') 0 0/25px 25px no-repeat;`;

    if (icon.color) {
      style += `background-color: ${icon.color};`;
    }

    return style;
  }

  protected handleDateError(event: string): void {
    this.dateError.emit(event);
  } 

  protected handleDateChange(event: string, row: any) {
    this.dateChanged.emit({ date: event, row });
  }

  protected emitDollarAmountChange(event: string, row: any): void {
    this.dollarAmountChanged.emit({amount: event, row})
  }
}
